export const WEBSITE_ANIMATION_KEYS = [
  "fade",
  "slide",
  "zoom",
  "light_reveal",
  "curtain_reveal",
  "blur_fade",
  "flip_3d",
  "swipe",
  "scale_blur_pop",
];

export const DEFAULT_ENTRY_DURATION_SEC = 2.0;
export const DEFAULT_PAGE_TRANSITION_DURATION_SEC = 1.0;

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const clampDurationSec = (raw, fallback) => {
  if (raw === null || raw === undefined) return fallback;
  const n = typeof raw === "number" ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n)) return fallback;
  return clamp(n, 0.5, 7.0);
};

export const isLightReveal = (type) => type === "light_reveal";

const withTransition = (variants, durationSec) => ({
  variants,
  transition: {
    duration: clampDurationSec(durationSec, 0.3),
    ease: [0.16, 1, 0.3, 1],
  },
});

const VARIANTS_BY_TYPE = {
  fade: (d) =>
    withTransition(
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      },
      d,
    ),
  slide: (d) =>
    withTransition(
      {
        initial: { opacity: 0, y: 28 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -28 },
      },
      d,
    ),
  zoom: (d) =>
    withTransition(
      {
        initial: { opacity: 0, scale: 0.985 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 1.015 },
      },
      d,
    ),
  // light_reveal is handled via an overlay; content motion can be a gentle fade.
  light_reveal: (d) => VARIANTS_BY_TYPE.fade(d),
  curtain_reveal: (d) =>
    withTransition(
      {
        initial: { clipPath: "inset(0 0 100% 0)", opacity: 1 },
        animate: { clipPath: "inset(0 0 0% 0)", opacity: 1 },
        exit: { clipPath: "inset(100% 0 0 0)", opacity: 1 },
      },
      d,
    ),
  blur_fade: (d) =>
    withTransition(
      {
        initial: { opacity: 0, filter: "blur(10px)" },
        animate: { opacity: 1, filter: "blur(0px)" },
        exit: { opacity: 0, filter: "blur(10px)" },
      },
      d,
    ),
  flip_3d: (d) =>
    withTransition(
      {
        initial: { opacity: 0, rotateX: -75, transformPerspective: 1200 },
        animate: { opacity: 1, rotateX: 0, transformPerspective: 1200 },
        exit: { opacity: 0, rotateX: 75, transformPerspective: 1200 },
      },
      d,
    ),
  swipe: (d) =>
    withTransition(
      {
        initial: { opacity: 0, x: -72 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 72 },
      },
      d,
    ),
  scale_blur_pop: (d) =>
    withTransition(
      {
        initial: { opacity: 0, scale: 0.92, filter: "blur(6px)" },
        animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
        exit: { opacity: 0, scale: 1.02, filter: "blur(6px)" },
      },
      d,
    ),
};

const safeType = (type) => {
  if (!type || typeof type !== "string") return null;
  if (!WEBSITE_ANIMATION_KEYS.includes(type)) return null;
  return type;
};

export const getEntryMotionProps = ({ type, duration }) => {
  const t = safeType(type);
  if (!t) {
    return { enabled: false, type: null, variants: null, transition: null };
  }
  const d = clampDurationSec(duration, DEFAULT_ENTRY_DURATION_SEC);
  const { variants, transition } = (VARIANTS_BY_TYPE[t] || VARIANTS_BY_TYPE.fade)(d);
  return { enabled: true, type: t, variants, transition };
};

export const getPageTransitionMotionProps = ({ type, duration }) => {
  const t = safeType(type);
  if (!t) {
    return { enabled: false, type: null, variants: null, transition: null };
  }
  const d = clampDurationSec(duration, DEFAULT_PAGE_TRANSITION_DURATION_SEC);
  const { variants, transition } = (VARIANTS_BY_TYPE[t] || VARIANTS_BY_TYPE.fade)(d);
  return { enabled: true, type: t, variants, transition };
};

